import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Truck, Phone, MapPin, CreditCard, Building2, AlertTriangle, ShoppingCart, Plus, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import api from '@/services/api';
import { useNavigate } from 'react-router-dom';

const paymentTermsLabels = {
  cash: 'Cash on Delivery',
  credit_7: 'Credit - 7 Days',
  credit_15: 'Credit - 15 Days',
  credit_30: 'Credit - 30 Days',
  credit_45: 'Credit - 45 Days',
  credit_60: 'Credit - 60 Days'
};

const OwnStoreLocalVendorsPage = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [storeInfo, setStoreInfo] = useState(null);

  const fetchLocalVendors = useCallback(async () => {
    try {
      const response = await api.get('/vendors/store/local-vendors');
      setVendors(response.data || []);
    } catch (error) {
      if (error.response?.status === 403) {
        toast({ 
          title: 'Access Denied', 
          description: 'This feature is for owned stores only. Franchise stores manage their own local vendors.',
          variant: 'destructive' 
        });
      } else {
        toast({ title: 'Error', description: 'Failed to fetch local vendors', variant: 'destructive' });
      }
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const fetchStoreInfo = useCallback(async () => {
    try {
      const response = await api.get(`/stores/${user?.entity_id}`);
      setStoreInfo(response.data);
    } catch (error) {
      console.error('Failed to fetch store info:', error);
    }
  }, [user?.entity_id]);

  useEffect(() => {
    fetchLocalVendors();
    fetchStoreInfo();
  }, [fetchLocalVendors, fetchStoreInfo]);

  const formatAddress = (address) => {
    if (!address) return '-';
    const parts = [address.address_line1, address.area, address.district || address.city, address.state].filter(Boolean);
    return parts.join(', ');
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <Toaster />
      
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Truck className="w-6 h-6 text-amber-600" /> Local Vendors
          </h1>
          <p className="text-gray-500">Vendors assigned by HQ for local purchases</p>
        </div>
      </div>

      {/* Info Banner */}
      <div className="mb-6 p-4 rounded-lg bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200">
        <div className="flex items-start gap-3">
          <Building2 className="w-5 h-5 text-amber-600 mt-0.5" />
          <div>
            <p className="font-medium text-amber-800">HQ Managed Local Vendors</p>
            <p className="text-sm text-amber-700 mt-1">
              These vendors are assigned by HQ for your store. You can create local purchase orders from these vendors.
              All payments are managed by HQ.
            </p>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading local vendors...</div>
      ) : vendors.length === 0 ? (
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="py-12 text-center">
            <AlertTriangle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
            <p className="text-gray-600 font-medium">No Local Vendors Assigned</p>
            <p className="text-gray-500 text-sm mt-2">
              HQ has not assigned any local vendors to your store yet. Contact HQ for vendor assignment.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {vendors.map(vendor => (
            <Card key={vendor.id} className="bg-white border-gray-200 shadow-sm hover:shadow-md transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-gray-800 text-lg">{vendor.name}</CardTitle>
                    <p className="text-sm text-teal-600 font-medium mt-1">{vendor.code}</p>
                  </div>
                  <Badge className="bg-amber-100 text-amber-700 flex items-center gap-1">
                    <Truck className="w-3 h-3" /> Local
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {vendor.gstin && (
                  <div className="text-xs text-gray-500">GSTIN: {vendor.gstin}</div>
                )}
                
                {vendor.contact?.phone && (
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600">{vendor.contact.phone}</span>
                  </div>
                )}

                {vendor.address && (
                  <div className="flex items-start gap-2 text-sm">
                    <MapPin className="w-4 h-4 text-gray-400 mt-0.5" />
                    <span className="text-gray-600">{formatAddress(vendor.address)}</span>
                  </div>
                )}

                <div className="flex items-center gap-2 text-sm">
                  <CreditCard className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-600">{paymentTermsLabels[vendor.payment_terms] || vendor.payment_terms}</span>
                </div>

                <div className="flex items-center justify-between pt-3 border-t border-gray-200">
                  <div>
                    <p className="text-xs text-gray-500">Outstanding</p>
                    <p className={`font-medium ${vendor.current_balance > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                      ₹{Math.abs(vendor.current_balance || 0).toLocaleString()}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => navigate(`/store/local-purchase/new?vendor_id=${vendor.id}`)}
                      className="text-amber-600 border-amber-200 hover:bg-amber-50"
                    >
                      <ShoppingCart className="w-4 h-4 mr-1" /> New PO
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default OwnStoreLocalVendorsPage;
